import { Component, computed, effect, inject, input, output } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { availableCountries } from '../../../../../../../../../common/common-types/country-codes';
import { SectionCardComponent } from '../../../../components/shared/section-card/section-card.component';
import { SaveBarComponent } from '../../../../components/shared/save-bar/save-bar.component';

/** The editable part of an org or venue customer. */
export interface CustomerFormValue {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address1: string;
    zipCode: string;
    city: string;
    country: string;
    isCompany: boolean;
    companyName: string;
    orgNo: string;
    notes: string;
    blackListed: boolean;
    excludeCrm: boolean;
}

@Component({
    selector: 'fb-customer-form',
    imports: [ReactiveFormsModule, TranslatePipe, InputTextModule, TextareaModule, ToggleSwitchModule, SelectModule, ButtonModule, SectionCardComponent, SaveBarComponent],
    templateUrl: './customer-form.component.html'
})
export class CustomerFormComponent {
    private fb = inject(FormBuilder);

    customer = input.required<Partial<CustomerFormValue> | null>();
    saving = input(false);
    /** Shown next to the restriction toggles, e.g. "Applies to this venue only". */
    restrictionsNote = input<string>();
    save = output<CustomerFormValue>();

    countries = [...availableCountries];

    form = this.fb.nonNullable.group({
        // Many customers only ever left an email or phone, so names can't be required here.
        firstName: [''],
        lastName: [''],
        email: ['', Validators.email],
        phone: [''],
        address1: [''],
        zipCode: [''],
        city: [''],
        country: [''],
        isCompany: [false],
        companyName: [''],
        orgNo: [''],
        notes: [''],
        blackListed: [false],
        excludeCrm: [false]
    });

    private status = toSignal(this.form.statusChanges, { initialValue: this.form.status });
    private values = toSignal(this.form.valueChanges, { initialValue: this.form.getRawValue() });
    isCompany = computed(() => !!this.values().isCompany);
    canSave = computed(() => this.status() === 'VALID' && !this.saving());
    // Re-evaluated on every value change; reset() also emits, so this drops back to false after a save.
    dirty = computed(() => {
        this.values();
        return this.form.dirty;
    });

    constructor() {
        effect(() => this.resetTo(this.customer()));
    }

    /** Puts the form back to the customer as last saved. */
    discard(): void {
        this.resetTo(this.customer());
    }

    private resetTo(c: Partial<CustomerFormValue> | null): void {
        if (!c) {
            return;
        }
        const country = String(c.country ?? '');
        // Stored codes vary in case; match the option list so the select shows the value.
        const match = this.countries.find((option) => option.code.toLowerCase() === country.toLowerCase());
        this.form.reset({
            firstName: c.firstName ?? '',
            lastName: c.lastName ?? '',
            email: c.email ?? '',
            phone: c.phone ?? '',
            address1: String(c.address1 ?? ''),
            zipCode: String(c.zipCode ?? ''),
            city: String(c.city ?? ''),
            country: match?.code ?? country,
            isCompany: !!c.isCompany,
            companyName: c.companyName ?? '',
            orgNo: c.orgNo ?? '',
            notes: c.notes ?? '',
            blackListed: !!c.blackListed,
            excludeCrm: !!c.excludeCrm
        });
    }

    invalid(field: keyof CustomerFormValue): boolean {
        const control = this.form.controls[field];
        return control.invalid && (control.dirty || control.touched);
    }

    submit(): void {
        this.form.markAllAsTouched();
        if (this.form.valid) {
            // The parent passes the saved customer back in, which resets the form to pristine.
            this.save.emit(this.form.getRawValue());
        }
    }
}
